/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useState } from 'react';
import { formatClockHm, formatGmtOffsetText, formatMeetingNo, formatTimeRangeHm, formatYearMonthDay, minutesBetween } from '../historyUtils';
import logger from '../logger';
import { PortalMessages as M } from '../messages';
import { formatDurationLabel } from '../scheduleUtils';
import { useIsPortalMobile } from '../useIsPortalMobile';
function CopyIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("rect", { height: '12', rx: '2', stroke: 'currentColor', strokeWidth: '1.7', width: '12', x: '8', y: '8' }),
        React.createElement("path", { d: 'M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2', stroke: 'currentColor', strokeWidth: '1.7' })));
}
function CloseIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("path", { d: 'M6 6l12 12M18 6 6 18', stroke: 'currentColor', strokeLinecap: 'round', strokeWidth: '1.8' })));
}
function BrandMark() {
    return (React.createElement("div", { "aria-hidden": true, className: 'portal-invite-brand-mark' }, "M"));
}
/**
 * 打开会议直链后的邀请确认页：手机全屏卡片，电脑居中弹窗。
 *
 * @param {IProps} props - Props.
 * @returns {React.ReactElement}
 */
export default function MeetingInvitePage({ busy, error, loginLabel, onClose, onJoin, preview }) {
    const isMobile = useIsPortalMobile();
    const [toast, setToast] = useState('');
    const startIso = preview.scheduledStart;
    const endIso = preview.scheduledEnd;
    const durationMin = minutesBetween(startIso, endIso);
    const displayNo = formatMeetingNo(preview.meetingNo, ' ') || preview.meetingNo;
    const organizerName = preview.organizerName || '';
    const isOngoing = preview.status === 'ONGOING';
    const statusLabel = isOngoing ? M.MEETING_ONGOING : M.MEETING_PENDING;
    const tzText = startIso
        ? `(${formatGmtOffsetText(startIso)})${-new Date(startIso).getTimezoneOffset() === 8 * 60 ? ` ${M.INVITE_TZ_CN}` : ''}`
        : '';
    useEffect(() => {
        logger.info(`[门户] ${new Date().toISOString()} 展示会议邀请页`
            + ` | meetingNo=${preview.meetingNo} status=${preview.status} mobile=${isMobile}`);
    }, [isMobile, preview.meetingNo, preview.status]);
    const showToast = (text) => {
        setToast(text);
        window.setTimeout(() => setToast(''), 1800);
    };
    const copyText = async (text, okMessage) => {
        try {
            await navigator.clipboard.writeText(text);
            showToast(okMessage);
            logger.info(`[门户] ${new Date().toISOString()} 邀请页已复制`
                + ` | meetingNo=${preview.meetingNo} len=${text.length}`);
        }
        catch (e) {
            logger.warn(`邀请页复制失败 | meetingNo=${preview.meetingNo} error=${e?.message || e}`);
        }
    };
    const copyMeetingNo = () => {
        if (!preview.meetingNo) {
            return;
        }
        copyText(preview.meetingNo, M.HISTORY_COPIED);
    };
    const copyInvite = () => {
        const timeRange = formatTimeRangeHm(startIso, endIso);
        const link = preview.meetingNo ? `${window.location.origin}/${preview.meetingNo}` : '';
        const lines = [
            preview.title || M.NO_TITLE,
            timeRange,
            preview.meetingNo ? `${M.MEETING_NO}：${displayNo}` : '',
            link
        ].filter(Boolean);
        copyText(lines.join('\n'), M.MEETING_SHARE_COPIED);
    };
    const onUnavailable = (label) => {
        logger.info(`[门户] ${new Date().toISOString()} 邀请页未开放能力`
            + ` | action=${label} meetingNo=${preview.meetingNo}`);
        showToast(M.INVITE_FEATURE_TODO);
    };
    const card = (React.createElement("div", { className: 'portal-invite-card' },
        React.createElement(BrandMark, null),
        React.createElement("div", { className: 'portal-invite-title' }, preview.title || M.NO_TITLE),
        React.createElement("div", { className: 'portal-invite-meeting-no' },
            React.createElement("span", null, displayNo),
            React.createElement("button", { "aria-label": M.HISTORY_COPY, className: 'portal-invite-copy-btn', onClick: copyMeetingNo, type: 'button' },
                React.createElement(CopyIcon, null))),
        React.createElement("div", { className: `portal-invite-status ${isOngoing ? 'is-ongoing' : 'is-pending'}` }, statusLabel),
        React.createElement("div", { className: 'portal-invite-timeline' },
            React.createElement("div", { className: 'portal-invite-time-col' },
                React.createElement("div", { className: 'clock' }, formatClockHm(startIso) || '--'),
                React.createElement("div", { className: 'date' }, formatYearMonthDay(startIso))),
            React.createElement("div", { className: 'portal-invite-time-mid' },
                React.createElement("div", { className: 'line' }),
                React.createElement("div", { className: 'duration' }, durationMin != null ? formatDurationLabel(durationMin) : '--')),
            React.createElement("div", { className: 'portal-invite-time-col is-end' },
                React.createElement("div", { className: 'clock' }, formatClockHm(endIso) || '--'),
                React.createElement("div", { className: 'date' }, formatYearMonthDay(endIso || startIso)))),
        tzText ? React.createElement("div", { className: 'portal-invite-tz' }, tzText) : null,
        organizerName ? (React.createElement("div", { className: 'portal-invite-organizer' },
            React.createElement("span", { className: 'avatar' }, organizerName.slice(0, 1)),
            React.createElement("span", { className: 'label' }, M.HISTORY_ORGANIZER),
            React.createElement("span", { className: 'name' }, organizerName))) : null));
    const actions = (React.createElement("div", { className: 'portal-invite-actions' },
        React.createElement("button", { className: 'portal-invite-outline-btn', disabled: busy, onClick: () => onUnavailable(M.INVITE_AI_HOST), type: 'button' }, M.INVITE_AI_HOST),
        React.createElement("button", { className: 'portal-invite-outline-btn', disabled: busy, onClick: () => onUnavailable(M.INVITE_PHONE_JOIN), type: 'button' }, M.INVITE_PHONE_JOIN),
        React.createElement("button", { className: 'portal-invite-outline-btn', disabled: busy, onClick: () => onUnavailable(M.INVITE_MINI_PROGRAM), type: 'button' }, M.INVITE_MINI_PROGRAM),
        React.createElement("button", { className: 'portal-invite-primary-btn', disabled: busy, onClick: onJoin, type: 'button' }, busy ? M.BUSY : M.JOIN_MEETING),
        React.createElement("button", { className: 'portal-invite-copy-invite', onClick: copyInvite, type: 'button' },
            M.INVITE_COPY_HINT_PREFIX,
            React.createElement("span", null, M.INVITE_COPY_HINT_LINK),
            M.INVITE_COPY_HINT_SUFFIX)));
    const chrome = (React.createElement("div", { className: 'portal-invite-chrome' },
        React.createElement("span", { className: 'portal-invite-lang' }, M.INVITE_LANG),
        loginLabel ? React.createElement("span", { className: 'portal-invite-login' }, loginLabel) : null));
    return (React.createElement("div", { className: `portal-invite-shell ${isMobile ? 'is-mobile' : 'is-desktop'}` },
        isMobile ? (React.createElement("header", { className: 'portal-invite-mobile-bar' },
            React.createElement("button", { "aria-label": M.CLOSE, className: 'portal-invite-icon-btn', onClick: onClose, type: 'button' },
                React.createElement(CloseIcon, null)),
            React.createElement("div", { className: 'portal-invite-mobile-brand' },
                React.createElement(BrandMark, null),
                React.createElement("span", null, M.INVITE_APP_NAME)),
            React.createElement("span", { className: 'portal-invite-icon-btn' }))) : null,
        React.createElement("div", { className: 'portal-invite-backdrop', onClick: isMobile ? undefined : onClose, role: 'presentation' },
            React.createElement("div", { className: 'portal-invite-panel', onClick: event => event.stopPropagation() },
                isMobile ? null : (React.createElement("button", { "aria-label": M.CLOSE, className: 'portal-invite-icon-btn portal-invite-desktop-close', onClick: onClose, type: 'button' },
                    React.createElement(CloseIcon, null))),
                chrome,
                card,
                error ? React.createElement("div", { className: 'portal-error' }, error) : null,
                actions)),
        toast ? React.createElement("div", { className: 'portal-invite-toast' }, toast) : null));
}
